import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { AppStore, RequestStatus, userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch } from '~/initializeStore';
import { formLayout } from '~/components/ui';
import AuthCard, { AuthHeading, AUTH_TITLE_ID } from '../../layout/AuthCard';
import AuthResult from '../../layout/AuthResult';
import PasswordInput from '../../form/PasswordInput';
import SubmitButton from '../../form/SubmitButton';
import { newPasswordRules } from '../../form/authRules';
import styles from '../../form/authForm.module.scss';

interface ChangePasswordModel {
  password: string;
  passwordConfirmation: string;
}

const LOGIN_PATH = '/auth/login';
const REDIRECT_SECONDS = 5;

/** Nowe hasło z linku w e-mailu (`?code=`). Po zmianie odliczanie do logowania; zły link → nowy link. */
export const ResetPassword: FunctionComponent = () => {
  const { t } = useTranslation(['auth']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const code = useQuery().get('code') ?? '';
  const { passwordStatus } = useSelector((store: AppStore) => store.user);
  const [seconds, setSeconds] = useState(REDIRECT_SECONDS);
  const busy = passwordStatus === RequestStatus.UPDATING;
  const done = passwordStatus === RequestStatus.SUCCESS;
  const failed = passwordStatus === RequestStatus.FAILED || !code;

  useEffect(() => {
    dispatch(userActions.resetErrors());
  }, [dispatch]);

  useEffect(() => {
    if (!done) return undefined;
    if (seconds <= 0) {
      history.push(LOGIN_PATH);

      return undefined;
    }
    const id = window.setTimeout(() => setSeconds((value) => value - 1), 1000);

    return () => window.clearTimeout(id);
  }, [done, seconds, history]);

  const onFinish = ({ password }: ChangePasswordModel) => {
    if (busy) return;
    dispatch(userActions.resetUserPassword({ resetPasswordCode: code, password }));
  };

  if (done) {
    return (
      <AuthCard>
        <AuthResult
          tone="done"
          title={t('auth:screens.reset.doneTitle')}
          text={t('auth:screens.reset.doneText', { seconds })}
          actions={
            <Button type="primary" block onClick={() => history.push(LOGIN_PATH)}>
              {t('auth:screens.reset.loginNow')}
            </Button>
          }
        />
      </AuthCard>
    );
  }

  if (failed) {
    return (
      <AuthCard footer={<Link to={LOGIN_PATH}>{t('auth:screens.sent.back')}</Link>}>
        <AuthResult
          tone="failed"
          title={t('auth:screens.reset.failedTitle')}
          text={t('auth:screens.reset.failedText')}
          actions={
            <Button type="primary" block onClick={() => history.push('/auth/forgot-password')}>
              {t('auth:screens.reset.newLink')}
            </Button>
          }
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard footer={<Link to={LOGIN_PATH}>{t('auth:screens.sent.back')}</Link>}>
      <AuthHeading title={t('auth:screens.reset.title')} />
      <Form<ChangePasswordModel>
        name="resetPassword"
        layout="vertical"
        requiredMark={false}
        scrollToFirstError={{ focus: true }}
        validateTrigger="onBlur"
        className={`${formLayout.form} ${styles.form}`}
        aria-labelledby={AUTH_TITLE_ID}
        onFinish={onFinish}
      >
        <Form.Item
          name="password"
          label={t('auth:screens.field.newPassword')}
          rules={newPasswordRules(t)}
          extra={t('auth:screens.field.passwordHint')}
        >
          <PasswordInput
            autoComplete="new-password"
            aria-required="true"
            autoFocus
            readOnly={busy}
          />
        </Form.Item>
        <Form.Item
          name="passwordConfirmation"
          label={t('auth:screens.field.confirmPassword')}
          dependencies={['password']}
          rules={[
            { required: true, message: t('auth:screens.validation.confirmRequired') },
            ({ getFieldValue }) => ({
              validator: (_rule, value: string) =>
                !value || getFieldValue('password') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error(t('auth:screens.validation.passwordMismatch'))),
            }),
          ]}
        >
          <PasswordInput autoComplete="new-password" aria-required="true" readOnly={busy} />
        </Form.Item>
        <SubmitButton
          label={t('auth:screens.reset.submit')}
          busyLabel={t('auth:screens.reset.busy')}
          busy={busy}
        />
      </Form>
    </AuthCard>
  );
};
